import type { ActionRowQuestion as ActionRowQuestionShape, ActionTextRun } from "~/shared/ai/action-block";
import { fillActionLabel } from "~/shared/ai/assistant-action-artifact";
import { ACTION_ACCENT } from "~/styles/palettes";
import { Box, Button, Checkbox, Flex, Text } from "~/ui";

import { ASSISTANT_ACTION_COPY } from "../assistant-action-copy";

import { BLOCK } from "./action-block-tokens";

/** The one amber place on a card: the words of an open question that blocks saving, in readable ink. */
const QUESTION_AREA = {
  fontSize: BLOCK.bodySize,
  lineHeight: "20px",
  color: BLOCK.ink,
  backgroundColor: BLOCK.warnBg,
  borderRadius: 6,
  padding: "8px 10px",
  marginBottom: 8,
} as const;

export type ActionClarifyPick = {
  itemKey: string;
  choiceId: string;
  label: string;
  /** The choice's own words for the record, when it sets them. */
  reply?: string;
};

const TextRuns = ({ runs }: { runs: readonly ActionTextRun[] }) => (
  <>
    {runs.map((run, index) =>
      run.strong ? (
        <span key={index} style={{ fontWeight: 600, color: BLOCK.ink }}>
          {run.text}
        </span>
      ) : (
        <span key={index}>{run.text}</span>
      ),
    )}
  </>
);

/**
 * The closed question a row asks, shown only with control. One-choice questions are answer buttons;
 * many-choice questions are checkboxes with a submit. A pick is sent as `{ itemKey, choiceId }` for
 * the server to map (staged in the pane); a reply is sent as the person's words.
 */
export const ActionRowQuestion = ({
  itemKey,
  subject,
  question,
  canControl,
  chosenSet,
  onToggleChoice,
  onClarify,
  onStructuredClarify,
  hideSubmit = false,
}: {
  itemKey: string;
  /** The row's subject, named in a pick's message. */
  subject: string;
  question: ActionRowQuestionShape;
  canControl: boolean;
  chosenSet: ReadonlySet<string>;
  onToggleChoice: (choiceId: string) => void;
  onClarify?: (query: string) => Promise<void>;
  onStructuredClarify?: (picks: readonly ActionClarifyPick[]) => Promise<void>;
  /** When true, staging is handled by a parent footer (batch review pane). */
  hideSubmit?: boolean;
}) => {
  if (question.select === "one") {
    const picks = question.answer === "pick";
    const answerable = picks ? hideSubmit || Boolean(onStructuredClarify ?? onClarify) : Boolean(onClarify);
    if (!canControl || !answerable) {
      return null;
    }
    // A pick is staged in the pane and sent as a pick on the card; a reply is the person's words.
    const answer = (choice: ActionRowQuestionShape["choices"][number]) => {
      if (!picks) {
        void onClarify?.(choice.reply ?? choice.label);
      } else if (hideSubmit) {
        onToggleChoice(choice.id);
      } else if (onStructuredClarify) {
        void onStructuredClarify([{ itemKey, choiceId: choice.id, label: choice.label, ...(choice.reply ? { reply: choice.reply } : {}) }]);
      } else {
        void onClarify?.(choice.reply ?? ASSISTANT_ACTION_COPY.chosenRowTurn(choice.label, subject));
      }
    };
    const answers = question.choices.filter(choice => !choice.quiet);
    const ways = question.choices.filter(choice => choice.quiet);
    return (
      <Box aria-label={question.label}>
        {question.summary && (
          <Text style={QUESTION_AREA}>
            <TextRuns runs={question.summary} />
          </Text>
        )}
        {question.alternative && (
          <Button size="xs" variant="light" mb={6} onClick={() => void onClarify?.(question.alternative?.reply ?? "")}>
            {question.alternative.label}
          </Button>
        )}
        {answers.length > 0 && (
          <Flex direction="column" align="flex-start" gap={6}>
            {answers.map(choice => {
              const chosen = picks && hideSubmit && chosenSet.has(choice.id);
              return (
                <Button
                  key={choice.id}
                  size="sm"
                  variant="default"
                  onClick={() => answer(choice)}
                  aria-pressed={picks && hideSubmit ? chosen : undefined}
                  style={{
                    height: "auto",
                    minHeight: 36,
                    padding: "6px 12px",
                    borderColor: chosen ? ACTION_ACCENT : BLOCK.border,
                    backgroundColor: chosen ? BLOCK.chosenBg : undefined,
                  }}
                >
                  <span style={{ fontSize: BLOCK.bodySize, fontWeight: 600, color: BLOCK.ink, fontVariantNumeric: "tabular-nums" }}>
                    {choice.label}
                  </span>
                  {choice.detail && (
                    <span style={{ fontSize: BLOCK.bodySize, fontWeight: 400, color: BLOCK.secondary, marginLeft: 10 }}>
                      {choice.detail}
                    </span>
                  )}
                </Button>
              );
            })}
          </Flex>
        )}
        {ways.length > 0 && (
          <Flex gap="md" mt={answers.length > 0 ? 6 : 0}>
            {ways.map(choice => (
              <Button key={choice.id} size="xs" variant="subtle" px={answers.length > 0 ? 4 : 0} onClick={() => answer(choice)}>
                {choice.label}
              </Button>
            ))}
          </Flex>
        )}
      </Box>
    );
  }

  const submit = () => {
    const chosen = question.choices.filter(choice => chosenSet.has(choice.id));
    if (chosen.length === 0) {
      return;
    }
    if (question.answer === "reply") {
      const replies = chosen.map(choice => choice.reply ?? choice.label).join(" and ");
      void onClarify?.(question.replyWrap ? `${question.replyWrap.before}${replies}${question.replyWrap.after}` : replies);
      return;
    }
    const picks: ActionClarifyPick[] = chosen.map(choice => ({
      itemKey,
      choiceId: choice.id,
      label: choice.label,
      ...(choice.reply ? { reply: choice.reply } : {}),
    }));
    if (onStructuredClarify) {
      void onStructuredClarify(picks);
      return;
    }
    const singlePick = picks.length === 1 ? picks[0] : undefined;
    void onClarify?.(
      singlePick
        ? (singlePick.reply ?? ASSISTANT_ACTION_COPY.chosenRowTurn(singlePick.label, subject))
        : ASSISTANT_ACTION_COPY.chosenRowsTurn(picks.map(pick => ({ subject, label: pick.label, reply: pick.reply }))),
    );
  };

  return (
    <Box aria-label={question.label}>
      {(question.prompt || question.summary) && (
        <Box style={QUESTION_AREA}>
          {question.prompt && <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.ink }}>{question.prompt}</Text>}
          {question.summary && (
            <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.ink }}>
              <TextRuns runs={question.summary} />
            </Text>
          )}
        </Box>
      )}
      <Flex direction="column" gap={6}>
        {question.choices.map(choice => (
          <Checkbox
            key={choice.id}
            checked={chosenSet.has(choice.id)}
            onChange={() => onToggleChoice(choice.id)}
            label={
              <Text style={{ fontSize: BLOCK.bodySize }}>
                {choice.label}
                {choice.detail && <span style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{choice.detail}</span>}
              </Text>
            }
          />
        ))}
      </Flex>
      {canControl && !hideSubmit && question.submit && (
        <Button
          size="xs"
          mt={8}
          disabled={chosenSet.size === 0}
          style={chosenSet.size === 0 ? undefined : { backgroundColor: ACTION_ACCENT }}
          onClick={submit}
        >
          {chosenSet.size > 1 ? fillActionLabel(question.submit.many, { count: chosenSet.size }) : question.submit.one}
        </Button>
      )}
    </Box>
  );
};

/** A question the whole review asks, answered by a reply. It renders disabled without control. */
export const ActionGroupQuestion = ({
  question,
  canControl,
  onClarify,
}: {
  question: ActionRowQuestionShape;
  canControl: boolean;
  onClarify?: (query: string) => Promise<void>;
}) => (
  <Box style={{ marginBottom: 16 }}>
    <Text style={{ fontSize: BLOCK.bodySize }}>{question.prompt}</Text>
    <Flex wrap="wrap" gap="xs" style={{ marginTop: 8 }}>
      {question.choices.map(choice => (
        <Button
          key={choice.id}
          size="sm"
          variant="light"
          disabled={!canControl || !onClarify}
          onClick={() => {
            if (onClarify) {
              void onClarify(choice.reply ?? choice.label);
            }
          }}
        >
          {choice.label}
        </Button>
      ))}
    </Flex>
  </Box>
);
